import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePersonas, usePersonaStatusUpdates } from '@/hooks/use-personas';
import { useAccountUsers } from '@/hooks/use-account-users';
import {
	PersonaEmptyState,
	PersonaGeneratingPage,
	CreatePersonaModal,
	PersonaCreatingDialog,
	PersonaCard,
	PersonaDetailSheet,
	getPersonaDescription
} from '@/modules/persona';
import type { Persona } from '@/types';
import { BRAND_PERSONA, PERSONA_SEARCH_PARAM } from '@/modules/gaps/fix-target-link';

const PersonaCardSkeleton = () => (
	<div className='rounded-2xl border p-4 flex flex-col gap-3'>
		<Skeleton className='h-4 w-32' />
		<Skeleton className='h-3 w-full' />
		<Skeleton className='h-3 w-3/4' />
		<Skeleton className='h-5 w-24 rounded-full' />
	</div>
);

export default function PersonaRoute() {
	const { data: personas, isLoading } = usePersonas();
	const { data: accountUsers } = useAccountUsers();
	usePersonaStatusUpdates();

	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [creatingDialog, setCreatingDialog] = useState<{ status: 'creating' | 'failed'; message?: string | null } | null>(
		null
	);
	const [searchParams] = useSearchParams();
	// A link can open one persona's details (a gap fix that targets it, or `brand` for the brand's
	// own); one not in this list opens nothing.
	const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(() =>
		searchParams.get(PERSONA_SEARCH_PARAM)
	);

	if (isLoading) {
		return (
			<div className='p-4 flex flex-col gap-4'>
				<Skeleton className='h-6 w-32' />
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
					{Array.from({ length: 3 }).map((_, i) => (
						<PersonaCardSkeleton key={i} />
					))}
				</div>
			</div>
		);
	}

	// Bridges the brief gap between the create mutation resolving and the
	// invalidated `personas` query refetching with the new (generating) doc.
	const hasActivePersonaJob = creatingDialog?.status === 'creating';

	if ((!personas || personas.length === 0) && hasActivePersonaJob) {
		return <PersonaGeneratingPage />;
	}

	if (!personas || personas.length === 0) {
		return (
			<>
				<PersonaEmptyState onGetStarted={() => setCreateModalOpen(true)} />
				<CreatePersonaModal
					open={createModalOpen}
					onOpenChange={setCreateModalOpen}
					onCreating={() => setCreatingDialog({ status: 'creating' })}
					onFailed={(message) => setCreatingDialog({ status: 'failed', message })}
				/>
				<PersonaCreatingDialog
					open={!!creatingDialog}
					onOpenChange={(open) => !open && setCreatingDialog(null)}
					status={creatingDialog?.status ?? 'creating'}
					errorMessage={creatingDialog?.message}
				/>
			</>
		);
	}

	const brandPersona = personas.find((p) => p.userId == null) ?? null;
	const agentPersonas = personas.filter((p) => p.userId != null);
	const selectedPersona =
		selectedPersonaId === BRAND_PERSONA
			? brandPersona
			: (personas.find((p) => p.id === selectedPersonaId) ?? null);

	const nameForPersona = (persona: Persona) => {
		if (persona.userId == null) return 'My Brand';
		return accountUsers?.find((u) => u.id === persona.userId)?.name ?? 'Agent';
	};

	return (
		<div className='p-4 flex flex-col gap-6'>
			<div className='flex items-center justify-between'>
				<h1 className='text-base font-semibold'>Persona</h1>
				<Button className='rounded-full gap-1' onClick={() => setCreateModalOpen(true)}>
					<Plus className='size-4' />
					Persona
				</Button>
			</div>

			{brandPersona && (
				<PersonaCard
					persona={brandPersona}
					name={nameForPersona(brandPersona)}
					description={getPersonaDescription(brandPersona)}
					onClick={(p) => setSelectedPersonaId(p.id)}
				/>
			)}

			{agentPersonas.length > 0 && (
				<div className='flex flex-col gap-3'>
					<h2 className='text-sm font-medium text-muted-foreground'>My Agents</h2>
					<div className='flex flex-wrap gap-4'>
						{agentPersonas.map((persona) => (
							<PersonaCard
								key={persona.id}
								persona={persona}
								name={nameForPersona(persona)}
								description={getPersonaDescription(persona)}
								onClick={(p) => setSelectedPersonaId(p.id)}
							/>
						))}
					</div>
				</div>
			)}

			<CreatePersonaModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				onCreating={() => setCreatingDialog({ status: 'creating' })}
				onFailed={(message) => setCreatingDialog({ status: 'failed', message })}
				brandPersona={brandPersona}
				agentPersonas={agentPersonas}
			/>
			<PersonaCreatingDialog
				open={!!creatingDialog}
				onOpenChange={(open) => !open && setCreatingDialog(null)}
				status={creatingDialog?.status ?? 'creating'}
				errorMessage={creatingDialog?.message}
			/>

			{selectedPersona && (
				<PersonaDetailSheet
					persona={selectedPersona}
					name={nameForPersona(selectedPersona)}
					open={!!selectedPersona}
					onOpenChange={(open) => !open && setSelectedPersonaId(null)}
				/>
			)}
		</div>
	);
}
